import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Compass } from 'lucide-react'
import { discoveryApi } from '@/lib/discoveryApi'
import { describeError } from '@/lib/auth'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import styles from './DiscoverySection.module.css'
import { Select } from '../Select'

/** Вкладка «Каталог»: публикация сервера, категория, теги, описание и условия. */
export function DiscoverySection({ guildId }: { guildId: string }) {
  const qc = useQueryClient()
  const settings = useQuery({ queryKey: ['discovery-settings', guildId], queryFn: () => discoveryApi.settings(guildId) })
  const categories = useQuery({ queryKey: ['discovery-categories'], queryFn: () => discoveryApi.categories(), staleTime: 3_600_000 })
  const [form, setForm] = useState<{ category: string; tags: string; description: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const data = settings.data
  const state = form ?? {
    category: data?.category ?? 'community',
    tags: (data?.tags ?? []).join(', '),
    description: data?.description ?? '',
  }
  const patch = (p: Partial<typeof state>) => setForm({ ...state, ...p })

  const publish = useMutation({
    mutationFn: () =>
      discoveryApi.publish(guildId, {
        category: state.category,
        tags: state.tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 3),
        description: state.description.trim(),
      }),
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['discovery-settings', guildId] })
      void qc.invalidateQueries({ queryKey: ['discovery'] })
    },
    onError: (err: unknown) => setError(describeError(err).message),
  })
  const unpublish = useMutation({
    mutationFn: () => discoveryApi.unpublish(guildId),
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['discovery-settings', guildId] })
      void qc.invalidateQueries({ queryKey: ['discovery'] })
    },
    onError: (err: unknown) => setError(describeError(err).message),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    publish.mutate()
  }

  const limits = categories.data?.limits
  const problems = data?.problems ?? []
  const busy = publish.isPending || unpublish.isPending

  return (
    <div>
      <h1 className={layer.sectionTitle}>Каталог</h1>
      <p className={layer.text}>
        Публичный сервер виден в каталоге Кличата: карточка с описанием, онлайном и тегами, вступление в один клик.
        Участники и история при этом остаются закрытыми до вступления.
      </p>

      {data?.state === 'removed' ? (
        <p className={styles.removed}>Сервер снят из каталога администрацией{data.removed_reason ? `: ${data.removed_reason}` : ''}.</p>
      ) : null}
      {data?.state === 'auto_hidden' ? (
        <p className={styles.removed}>Карточка временно скрыта из-за жалоб и вернётся после разбора.</p>
      ) : null}
      {data?.listed ? (
        <p className={styles.listed}><Compass size={16} /> Сервер в каталоге · адрес {data.url}</p>
      ) : null}

      {problems.length > 0 ? (
        <div className={styles.problems}>
          <strong>Чтобы попасть в каталог:</strong>
          <ul>{problems.map((p) => <li key={p.key}>{p.text}</li>)}</ul>
        </div>
      ) : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      <form onSubmit={submit}>
        <Field label="Категория" htmlFor="disc-cat" hint="По ней сервер ищут в каталоге">
          <Select id="disc-cat" value={state.category} options={(categories.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))} onChange={(v) => patch({ category: v })} />
        </Field>
        <Field label="Теги" htmlFor="disc-tags" hint={`Через запятую, не больше ${limits?.tags ?? 3}`}>
          <input id="disc-tags" className={auth.input} value={state.tags} onChange={(e) => patch({ tags: e.target.value })} placeholder="музыка, кино, общение" />
        </Field>
        <Field
          label="Описание"
          htmlFor="disc-desc"
          hint={`От ${limits?.description_min ?? 40} до ${limits?.description_max ?? 300} символов · сейчас ${state.description.trim().length}`}
        >
          <textarea id="disc-desc" className={auth.input} rows={4} maxLength={limits?.description_max ?? 300} value={state.description} onChange={(e) => patch({ description: e.target.value })} />
        </Field>
        <div className={styles.actions}>
          <button type="submit" className={layer.button} disabled={busy || data?.state === 'removed'}>
            {data?.listed ? 'Сохранить карточку' : 'Опубликовать в каталоге'}
          </button>
          {data?.listed ? (
            <button type="button" className={styles.link} disabled={busy} onClick={() => unpublish.mutate()}>Убрать из каталога</button>
          ) : null}
        </div>
      </form>
    </div>
  )
}
